import QuizStatusBadge from 'components/badge/QuizStatusBadge';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { addDays, addMonths, format } from 'date-fns';
import { AppHelper } from 'helpers/appHelper';
import { QuizHelper } from 'helpers/quizHelper';
import { useApiState } from 'hooks/useApiState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';
import { QuizReportRefResponseType } from 'types/api/quizReportRefResponseType';
import WsModalReportDownload from './component/modal/WsModalReportDownload';
import WsModalReportTerm from './component/modal/WsModalReportTerm';
import WsReportChart from './component/pages/report/WsReportChart';

/**
 *　クイズレポート詳細画面
 */
export default function WsReportQuiz(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { quizCd } = useParams();

  const [acctState] = useRecoilState(AcctState);

  const { postQuizReportRef } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [quizState, setQuizState] = useState<QuizReportRefResponseType>(null);
  const [reportDateFromState, setReportDateFromState] = useState('');
  const [reportDateToState, setReportDateToState] = useState('');
  const [modalReportTermOpenState, setModalReportTermOpenState] = useState(false);
  const [modalReportDownloadOpenState, setModalReportDownloadOpenState] = useState(false);

  const isExpiredPlan =
    acctState.company.plan.planTypeDiv === DivConst.items.planType.expiredTrial.value ||
    acctState.company.plan.planTypeDiv === DivConst.items.planType.expiredBasic.value;

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    // ステートの初期化
    _initState();
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　コンテンツ確認ボタン押下時の処理
   */
  const onContentOpen = () => {
    if (quizState.quizStatusDiv === DivConst.items.quizStatus.published.value) {
      QuizHelper.openProductionUrl(quizState.cd);
    } else {
      navigate(`/ws/${acctState.company.cd}/quiz/edit/${quizState.cd}`);
    }
  };

  /**
   *　レポート出力ボタン押下時の処理
   */
  const onhModalReportDownloadOpen = () => {
    if (isExpiredPlan) {
      // プラン（利用期間切れ）の場合は処理しない
      return;
    }
    setModalReportDownloadOpenState(true);
  };

  /**
   *　集計期間変更ボタン押下時の処理
   */
  const onhModalReportTermOpen = () => {
    setModalReportTermOpenState(true);
  };

  /**
   * 集計期間変更モーダルで期間を変更した場合の処理
   */
  const onChangeReportTerm = async (reportDateFrom: string, reportDateTo: string) => {
    try {
      setProcessing(true);

      // クイズ情報を検索します。
      const response = await postQuizReportRef({
        quizCd: quizCd,
        reportDateFrom: reportDateFrom,
        reportDateTo: reportDateTo,
      });

      // ステートの更新
      setReportDateFromState(reportDateFrom);
      setReportDateToState(reportDateTo);

      setQuizState(response);
    } catch (e) {
      showBoundary(e);
    } finally {
      setProcessing(false);
    }
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画面の初期化処理を行います。
   */
  const _initState = async () => {
    try {
      // デフォルトの表示期間
      const toDate = addDays(new Date(), -1);
      const reportDateFrom = format(addMonths(toDate, -1), 'yyyy/MM/dd');
      // 現在日付の前日
      const reportDateTo = format(toDate, 'yyyy/MM/dd');

      // クイズ情報を検索します。
      const response = await postQuizReportRef({
        quizCd: quizCd,
        reportDateFrom: reportDateFrom,
        reportDateTo: reportDateTo,
      });

      // ステートの更新
      setReportDateFromState(reportDateFrom);
      setReportDateToState(reportDateTo);
      setQuizState(response);
    } catch (e) {
      showBoundary(e);
    } finally {
      setInitialized(true);
    }
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* 初期化中の表示 */}
      <IndicatorScreen visible={!initialized || processing} />
      {/* 初期化後の表示 */}
      {initialized && quizState && (
        <>
          <section className="section-header report-detail">
            <div className="container">
              <a onClick={() => navigate(-1)} className="btn-back-list hover">
                戻る
              </a>

              <div className="card card-detail">
                <div className="card-body">
                  <div className="d-md-flex d-flex-detail">
                    <div className="flex-shrink-0 flex-left">
                      <dl className="dl-list-indent">
                        <dt>
                          {/* クイズ画像の表示 */}
                          <div className="flex-shrink-0 flex-img" style={{ width: '75px' }}>
                            <img
                              src={AppHelper.getContentImageUrl(quizState.imageKey)}
                              className="img-fluid"
                            />
                          </div>
                        </dt>

                        <dd>
                          {/* クイズタイトルの表示 */}
                          <p className="caption">{quizState.title}</p>
                          {/* 登録日時の表示 */}
                          <span className="date">
                            {AppHelper.formatTimeToDate(
                              quizState.createdAt,
                              'yyyy年M月dd日 h:mm a'
                            )}
                          </span>
                          {/* クイズステータスの表示 */}
                          <QuizStatusBadge quizStatusDiv={quizState.quizStatusDiv} />
                        </dd>
                      </dl>
                    </div>
                    <div className="flex-grow-1 flex-right">
                      <a
                        onClick={onContentOpen}
                        className="btn btn-common btn-common-w238 moderate-blue"
                      >
                        コンテンツを確認
                      </a>
                      <a
                        onClick={onhModalReportDownloadOpen}
                        className={
                          isExpiredPlan
                            ? 'btn btn-disabled btn-common-w238 outline-blue'
                            : 'btn btn-common btn-common-w238 outline-blue'
                        }
                        data-tooltip-id="custom-tooltip"
                        data-tooltip-content="プランの期限がきれました。プランのアップグレードをご検討ください。"
                      >
                        レポート出力
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="section-header user-detail">
              <div className="container">
                <div className="d-md-flex d-flex-header card-detail">
                  <div className="flex-shrink-0 flex-left"></div>
                  <div className="flex-grow-1 flex-right">
                    <ul className="list-inline list-btn mb-0">
                      <ul className="list-unstyled list-inline mt-t">
                        <li className="list-inline-item me-1">
                          <b>集計期間</b>：{reportDateFromState} -{reportDateToState}
                        </li>
                        <li className="list-inline-item">
                          <button onClick={onhModalReportTermOpen} className="btn btn-simple blue">
                            変更
                          </button>
                        </li>
                      </ul>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section-statistical">
            <div className="container">
              <div className="row row-15-xl row-5-xl row-12-lg">
                <div className="col-md-3">
                  <div className="card card-white">
                    <div className="card-body">
                      <div className="d-flex d-flex-statistical">
                        <div className="flex-shrink-0 flex-left">
                          <small>参加数</small>
                          {AppHelper.formatNumberToCurrency(quizState.participatedCount)}
                        </div>
                        <div className="flex-grow-1 flex-right">
                          <span className="img-body light-blue">
                            <img src="/img/icon/people.svg" alt="参加数" className="w-lg-26" />
                          </span>
                        </div>
                      </div>

                      {/* <p className="caption">
                        昨日より{" "}
                        <span className="icon-ani">
                          <img src="/img/icon/arrow-up.svg" alt="" />
                        </span>
                        <span className="text-blue number">8.5%</span>上昇
                      </p> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card card-white">
                    <div className="card-body">
                      <div className="d-flex d-flex-statistical">
                        <div className="flex-shrink-0 flex-left">
                          <small>獲得リスト数</small>
                          {AppHelper.formatNumberToCurrency(quizState.mailAddressCount)}
                        </div>
                        <div className="flex-grow-1 flex-right">
                          <span className="img-body light-yellow">
                            <img src="/img/icon/arrow.svg" alt="獲得リスト数" className="w-lg-26" />
                          </span>
                        </div>
                      </div>

                      {/* <p className="caption">
                        先週より{" "}
                        <span className="icon-ani">
                          <img src="/img/icon/arrow-up.svg" alt="" />
                        </span>
                        <span className="text-blue number">1.3%</span>上昇
                      </p> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card card-white">
                    <div className="card-body">
                      <div className="d-flex d-flex-statistical">
                        <div className="flAddressink-0 flex-left">
                          <small>リスト獲得率</small>
                          {`${AppHelper.formatRateToPercent(
                            quizState.mailAddressAcquisitionRate
                          )}%`}
                        </div>
                        <div className="flex-grow-1 flex-right">
                          <span className="img-body light-green">
                            <img src="/img/icon/chart.svg" alt="リスト獲得率" className="w-lg-23" />
                          </span>
                        </div>
                      </div>

                      {/* <p className="caption">
                        昨日より{" "}
                        <span className="icon-ani">
                          <img src="/img/icon/arrow-down.svg" alt="" />
                        </span>
                        <span className="text-pink number">4.3%</span>下降
                      </p> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card card-white">
                    <div className="card-body">
                      <div className="d-flex d-flex-statistical">
                        <div className="flex-shrink-0 flex-left">
                          <small>完了率</small>
                          {`${AppHelper.formatRateToPercent(quizState.answeredRate)}%`}
                        </div>
                        <div className="flex-grow-1 flex-right">
                          <span className="img-body light-pink">
                            <img src="/img/icon/oclock.svg" alt="完了率" className="w-lg-23" />
                          </span>
                        </div>
                      </div>

                      {/* <p className="caption">
                        昨日より{" "}
                        <span className="icon-ani">
                          <img src="/img/icon/arrow-up.svg" alt="" />
                        </span>
                        <span className="text-blue number">1.8%</span>上昇
                      </p> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/*　レポートチャート  */}
          <WsReportChart reports={quizState.reports} />
        </>
      )}

      {/* レポート期間設定モーダル */}
      <WsModalReportTerm
        open={modalReportTermOpenState}
        setOpen={setModalReportTermOpenState}
        reportDateFrom={reportDateFromState}
        reportDateTo={reportDateToState}
        onSubmit={attr => onChangeReportTerm(attr.reportDateFrom, attr.reportDateTo)}
      />
      {/* レポートダウンロードモーダル */}
      <WsModalReportDownload
        open={modalReportDownloadOpenState}
        setOpen={setModalReportDownloadOpenState}
        cd={quizCd}
        contentsType={DivConst.items.contentsType.quiz.value}
        reportDateFrom={null}
        reportDateTo={null}
      />
      {
        // プラン（利用期間切れ）の場合に表示する
        isExpiredPlan && <Tooltip id="custom-tooltip" place="top" variant="info" />
      }
    </>
  );
}
